import { World, Bodies, DistanceJoint, type Body } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";

/**
 * Newton's cradle — the executive desk toy. Five elastic balls hang in a row,
 * just touching. Lift the end ball and let it swing: the impulse rips through the
 * stationary balls and kicks the far one out, swing for swing. It's the classic
 * test of clean restitution and momentum transfer through resting contacts.
 * Grab any ball to play conductor. (Matter.js ships this as a signature demo.)
 */
export const newtonsCradle: Demo = {
	id: "cradle",
	title: "Newton's cradle",
	blurb:
		"Five elastic balls hang touching in a row; pull one end out and the momentum jumps through the line to fling the far ball up. A momentum-transfer showcase for the restitution solver — grab a ball to drive it.",
	category: "Joints & Machines",
	build(): Scene {
		const world = new World({ gravity: [0, 9.81] });

		const count = 5;
		const r = 0.34;
		const ropeLen = 3.4;
		const anchorY = -ropeLen; // anchors sit a rope-length above the rest row
		const x0 = -((count - 1) * r); // centered; balls touch (spacing = 2r)

		const balls: Body[] = [];
		const anchors: [number, number][] = [];
		for (let i = 0; i < count; i++) {
			const ax = x0 + i * 2 * r;
			const ball = world.add(
				Bodies.circle({
					position: [ax, 0],
					radius: r,
					density: 1,
					restitution: 1, // perfectly elastic — momentum passes straight through
					friction: 0,
				}),
			);
			balls.push(ball);
			anchors.push([ax, anchorY]);
			// Rigid rope from the ball up to its fixed pivot (bodyB = null world point).
			world.addConstraint(
				new DistanceJoint(ball, null, { localAnchorB: [ax, anchorY], length: ropeLen }),
			);
		}

		// Lift the leftmost ball out to ~46° and release it.
		const theta = 0.8;
		const first = balls[0]!;
		const [fax, fay] = anchors[0]!;
		first.setPosition([fax - ropeLen * Math.sin(theta), fay + ropeLen * Math.cos(theta)]);

		// No air damping, so the cradle keeps clacking; keep the balls awake.
		const onBeforeStep = (): void => {
			for (const b of balls) b.wake();
		};

		// Draw the support beam the ropes hang from.
		const draw = (ctx: CanvasRenderingContext2D, cam: Camera): void => {
			const lx = anchors[0]![0] - r;
			const rx = anchors[count - 1]![0] + r;
			const y = anchorY;
			ctx.strokeStyle = "#8a93a8";
			ctx.lineWidth = 6;
			ctx.lineCap = "round";
			ctx.beginPath();
			ctx.moveTo(cam.worldToScreenX(lx), cam.worldToScreenY(y));
			ctx.lineTo(cam.worldToScreenX(rx), cam.worldToScreenY(y));
			ctx.stroke();
		};

		return {
			world,
			camera: new Camera(0, -2, 70),
			onBeforeStep,
			draw,
			hint: "Pull the end ball out and release — the impulse leaps through the line. Grab any ball to drive the cradle.",
		};
	},
};
